import { IonLoading } from "@ionic/react";
import { useAppStateListener } from "@src/app/appInit/useAppStateListener";
import { useFetchAndInitializeWorker } from "@src/app/appInit/useFetchAndInitializeWorker";
import { useIsAuthenticated } from "@src/app/appInit/useIsAuthenticated";
import { GlobalBanners } from "@src/app/routing/home/globalBanners";
import { useNotifyDeletionRequestCancelled } from "@src/appV2/Accounts/DeleteAccount/useNotifyDeletionRequestCancelled";
import { RootPaths } from "@src/appV2/App/paths";
import { PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME } from "@src/appV2/App/useSetPreferredPersistenceForAuth/constants";
import { useCognitoAuthSync } from "@src/appV2/Auth/cognito/useCognitoAuthSync";
import { ImpersonatePage } from "@src/appV2/Auth/Impersonation/ImpersonatePage";
import { AuthRedirect } from "@src/appV2/Auth/LoginSignUp/AuthRedirect";
import { GenericErrorPage } from "@src/appV2/Errors/Generic";
import { NotFoundPage } from "@src/appV2/Errors/NotFound";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { identifyUser } from "@src/appV2/FeatureFlags/identifyUser";
import { useCbhFlags } from "@src/appV2/FeatureFlags/useCbhFlags";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { UXCamSdk } from "@src/appV2/lib/UXCamSdk/UXCamSdk";
import { hideSplashScreen } from "@src/appV2/NativeAdapters/splashScreen.adapter";
import { setStatusBarStyle } from "@src/appV2/NativeAdapters/statusBar.adapter";
import { isRebrandingEnabled } from "@src/appV2/redesign/Rebranding/workerAppRebranding";
import { ShiftFeedbackResolver } from "@src/appV2/ShiftFeedback";
import { useHyperTrack } from "@src/appV2/Tracking/HyperTrack";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useWorkerDocumentsByStatus } from "@src/appV2/Worker/useWorkerDocumentsByStatus";
import { USER_EVENTS } from "@src/constants";
import { loadCustomFlags } from "@store/session";
// Rule disabled since this is where we identify the current user to LaunchDarkly
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from "launchdarkly-react-client-sdk";
import { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";

import { GlobalRouterPath } from "./constant/globalRoute";
import { HomeRoutes } from "./home";
import { AppRouterProps } from "./model";
import { PrivateRoute } from "./privateRoute";
import { PublicPage } from "./PublicPage";
import { AppVersion } from "../appVersion";
import { useDynamicLinkHandler } from "../dynamicLink";
import { initDatadogRum } from "../utils/datadog";
import { WelcomeRoute } from "../welcome/route";
import { TabRouterPath } from "./constant/tabRoute";

export const AppRouter: FC<AppRouterProps> = ({
  isVerifying,
  isLoggingOut,
  authStateHasLoaded,
  userId,
  ...props
}) => {
  const { isAuthorized } = props;
  const dispatch = useDispatch();
  const ldFlags = useCbhFlags();
  const disableUxCamRecording = ldFlags?.[CbhFeatureFlag.DISABLE_UX_CAM];

  // disabling the query since we want to only listen for changes here, without fetching
  const { data: worker } = useGetWorker({ enabled: false });
  const documentsByStatus = useWorkerDocumentsByStatus();

  useEffect(() => {
    if (!isCapacitorPlatform()) {
      return;
    }

    void setStatusBarStyle("Dark");
  }, []);

  useEffect(() => {
    if (isRebrandingEnabled()) {
      void hideSplashScreen();
    }
  }, []);

  useEffect(() => {
    let timeoutId;
    let intervalId;
    let intervalCount = 0;
    const interval = 10000;
    const maxIntervals = 6; // Stop after 60 seconds (6 * 10000ms)

    if (!authStateHasLoaded) {
      logEvent(APP_V2_APP_EVENTS.SPLASH_SCREEN_HIDING_SKIPPED_DUE_TO_AUTH_STATE, {
        authStateHasLoaded,
        authPersistence: localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME),
      });
      intervalId = setInterval(() => {
        intervalCount++;
        logEvent(APP_V2_APP_EVENTS.STUCK_AT_SPLASH_SCREEN_DUE_TO_AUTH_STATE, {
          interval: intervalCount * interval,
          authPersistence: localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME),
        });

        if (intervalCount >= maxIntervals) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, interval);
    } else {
      logEvent(APP_V2_APP_EVENTS.SPLASH_SCREEN_HIDE_TIMEOUT_SET, {
        authPersistence: localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME),
      });
      timeoutId = setTimeout(() => {
        logEvent(APP_V2_APP_EVENTS.SPLASH_SCREEN_HIDDEN_DUE_TO_AUTH_STATE, {
          authPersistence: localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME),
        });
        if (!isRebrandingEnabled()) {
          void hideSplashScreen();
        }
      }, 200);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [authStateHasLoaded]);

  useEffect(() => {
    void initDatadogRum();
    loadCustomFlags(dispatch);
    logEvent(USER_EVENTS.APP_LAUNCHED);
  }, [dispatch]);

  useEffect(() => {
    if (disableUxCamRecording) {
      UXCamSdk.stopSessionAndUploadData();
    }
    /**
     * FIXME - We don't have LD details when we initialize UXCam
     * so stopping it here after its initialization
     * Refactor this to stop UXCam initialization based on FF value
     */
  }, [disableUxCamRecording]);

  useAppStateListener();
  const isAuthenticated = useIsAuthenticated();

  // TODO: remove this event after investigation is complete (Ron Karroll)
  useEffect(() => {
    if (isAuthenticated) {
      logEvent(APP_V2_APP_EVENTS.IS_AUTHENTICATED_IN_FIREBASE_PRIOR_TO_FETCH_AND_INITIALIZE_WORKER);
    }
  }, [isAuthenticated]);

  // TODO: remove this event after investigation is complete (Ron Karroll)
  useEffect(() => {
    if (isAuthorized) {
      logEvent(APP_V2_APP_EVENTS.IS_AUTHORIZED_PRIOR_TO_FETCH_AND_INITIALIZE_WORKER);
    }
  }, [isAuthorized]);

  useFetchAndInitializeWorker(isAuthenticated, isAuthorized);

  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    void (async () => {
      await identifyUser({
        inputForContext: {
          worker,
          documentsByStatus,
        },
        client: ldClient,
      });
    })();
    // The worker object gets updated without changing its reference, so we have to memo each field separately.
  }, [
    ldClient,
    worker,
    worker?.userId,
    worker?.name,
    worker?.email,
    worker?.stage,
    worker?.createdAt,
    worker?.address?.metropolitanStatisticalArea,
    worker?.address?.city,
    worker?.address?.state,
    worker?.address?.stateCode,
    worker?.address?.countryCode,
    worker?.preference?.qualification,
    worker?.licensesData,
    worker?.homeHealthEnabled,
    documentsByStatus,
    dispatch,
  ]);

  useHyperTrack(userId);
  useDynamicLinkHandler();
  useNotifyDeletionRequestCancelled();
  useCognitoAuthSync();

  return (
    <>
      <AppVersion />
      <div className="cbhAppV1">
        <GlobalBanners />
        <Switch>
          <Route exact path="/" render={() => <Redirect to={GlobalRouterPath.HOME} />} />
          <Route
            exact
            path={GlobalRouterPath.LINK}
            render={() => <Redirect to={GlobalRouterPath.HOME} />}
          />
          <Route
            path={TabRouterPath.SHIFT_FEEDBACK_RESOLVER}
            component={ShiftFeedbackResolver}
            exact
          />
        </Switch>
      </div>

      <Route exact path={RootPaths.NOT_FOUND} component={NotFoundPage} />
      <Route exact path={RootPaths.ERROR} component={GenericErrorPage} />
      <Route exact path={RootPaths.AUTH_REDIRECT} component={AuthRedirect} />
      <Route exact path={RootPaths.IMPERSONATE} component={ImpersonatePage} />

      <PrivateRoute
        path={[GlobalRouterPath.HOME, GlobalRouterPath.APP_V2_HOME]}
        component={HomeRoutes}
        authStateHasLoaded={authStateHasLoaded}
        isLoggingOut={isLoggingOut}
        {...props}
      />
      <div className="cbhAppV1">
        <Route path={GlobalRouterPath.WELCOME}>
          <PublicPage authStateHasLoaded={authStateHasLoaded} isAuthorized={isAuthorized}>
            <WelcomeRoute />
          </PublicPage>
        </Route>

        <IonLoading isOpen={isVerifying} message="Logging in..." />
        <IonLoading isOpen={isLoggingOut} message="Logging out..." />
      </div>
    </>
  );
};
